var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useMemo, useState, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { OGTable } from '@opengear/table';
import { OGLink } from '@opengear/links';
import Box from '@mui/material/Box';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import ErrorIcon from '@mui/icons-material/Error';
import { OGButton } from '@opengear/buttons';
import { OGTagIcon } from '@opengear/icons/generic/tag';
import Stack from '@mui/material/Stack';
import { PortStateCell } from '@/components/ports/port/port-state-cell';
import { WebTerminalCell } from '@/components/ports/port/web-terminal-cell';
import { SshAccessCell } from '@/components/ports/port/ssh-access-cell';
import { usePaginationModel } from '@/hooks/pagination-model';
import { usePorts } from '@/api/ports/ports';
import { useResourceTagsNames } from '@/api/tags/resource-tags/names';
import { useSortModel } from '@/hooks/sort-model';
import { NODES_ROUTE } from '@/constants/routes';
import { NodeStatus, nodeStatusToQuery, useJson } from '@/utils/query';
import { FreeTextSearch, useFreeTextSearch } from '@/components/free-text-search';
import { IQueryPortField } from '../../../types/query/types';
import { ChipPopover } from '@/components/chip-popover';
import { NewTagButton } from '@/components/new-tag-button';
import { PortFilter, usePortFilterSearchParams } from '@/containers/filters/advanced-filters/port-filter';
import { NodeFilter, useNodeFilterSearchParams } from '@/containers/filters/advanced-filters/node-filter';
import { PortStatusFilter, usePortStatusFilter } from './port-status-filter';
import { useOpen } from '@/hooks/open';
import { AssignTagModal } from '../assign-tag-modal';
// Table constants
var fieldNames = [IQueryPortField.id, IQueryPortField.label, IQueryPortField.tag];
var nodeStatusQuery = nodeStatusToQuery(NodeStatus.Enrolled, 'node:');
export var PortsTable = memo(function (_a) {
    var permissions = _a.permissions;
    var t = useTranslation().t;
    var canEditTags = permissions.hasTagsEditAccess, isPrimary = permissions.isPrimary;
    var _b = __read(useState([]), 2), rowSelectionModel = _b[0], setRowSelectionModel = _b[1];
    // Table controls
    var _c = __read(usePaginationModel('ports'), 3), paginationModel = _c[0], handlePaginationModelChange = _c[1], pageSizeOptions = _c[2];
    var _d = __read(useSortModel('_id'), 3), defaultSortModel = _d[0], sortQueryParams = _d[1], handleSortModelChange = _d[2];
    // Port filter
    var _e = usePortFilterSearchParams(), portFilter = _e.portFilter, setPortFilter = _e.setPortFilter;
    // Node filter
    var _f = useNodeFilterSearchParams('node'), nodeFilter = _f.nodeFilter, nodeQueryObject = _f.nodeQueryObject, setNodeFilter = _f.setNodeFilter;
    // Port Status filter
    var _g = usePortStatusFilter(), portStatusFilter = _g.portStatusFilter, statusPortsParamValue = _g.statusPortsParamValue, setStatusPortsParamValue = _g.setStatusPortsParamValue;
    // Free text search
    var _h = useFreeTextSearch(fieldNames), textQueryObject = _h.textQueryObject, dispatchTextQuery = _h.dispatchTextQuery;
    // Aggregate all query items into one json query string
    var json = useJson(portFilter === null || portFilter === void 0 ? void 0 : portFilter.queryObject, nodeQueryObject, nodeStatusQuery, textQueryObject, portStatusFilter);
    var queryParams = useMemo(function () { return (__assign({ page: paginationModel.page + 1, per_page: paginationModel.pageSize, json: json }, sortQueryParams)); }, [json, paginationModel.page, paginationModel.pageSize, sortQueryParams]);
    // Loading data
    var _j = usePorts(queryParams), portsData = _j.data, isPortsDataLoading = _j.isLoading;
    var _k = useResourceTagsNames({ order_by: 'name' }), tagNamesData = _k.data, isTagNamesLoading = _k.isLoading;
    // Derived table values
    var _l = useMemo(function () { return ({
        loading: isPortsDataLoading || isTagNamesLoading,
        rowCount: portsData === null || portsData === void 0 ? void 0 : portsData.meta.total_count,
        portsRows: (portsData === null || portsData === void 0 ? void 0 : portsData.ports.map(function (port) { return (__assign(__assign({}, port), { assignedTags: port.resource_tags.map(function (tag_id) {
                var _a;
                return ({
                    id: tag_id,
                    resource_tag_name: ((_a = tagNamesData === null || tagNamesData === void 0 ? void 0 : tagNamesData.resourceTags.find(function (tag) { return tag.id === tag_id; })) === null || _a === void 0 ? void 0 : _a.resource_tag_name) || tag_id,
                });
            }) })); })) || [],
    }); }, [portsData === null || portsData === void 0 ? void 0 : portsData.meta.total_count, portsData === null || portsData === void 0 ? void 0 : portsData.ports, isPortsDataLoading, isTagNamesLoading, tagNamesData]), loading = _l.loading, rowCount = _l.rowCount, portsRows = _l.portsRows;
    // Assign Tags
    var assignTagModalOpen = useOpen();
    var _m = __read(useState([]), 2), modalRowData = _m[0], setModalRowData = _m[1];
    var selectedRows = useMemo(function () { return portsRows.filter(function (row) { return rowSelectionModel.includes(row.id); }); }, [portsRows, rowSelectionModel]);
    var assignTagsToRowClickHandler = useCallback(function (row) {
        setModalRowData([{ id: row.id, label: row.label, assignedTags: row.assignedTags }]);
        assignTagModalOpen.handleOpen();
    }, [assignTagModalOpen]);
    var assignTagsToSelectedRowsClickHandler = useCallback(function () {
        setModalRowData(selectedRows.map(function (row) { return ({ id: row.id, label: row.label, assignedTags: row.assignedTags }); }));
        assignTagModalOpen.handleOpen();
    }, [assignTagModalOpen, selectedRows]);
    var uncheckColumnCheckboxes = useCallback(function () {
        setRowSelectionModel([]);
    }, [setRowSelectionModel]);
    var renderPortLabel = useCallback(function (params) {
        var portNumber = null;
        if (params.row.port_csid) {
            var portNumberMatch = params.row.port_csid.match(/\d+$/);
            portNumber = portNumberMatch === null || portNumberMatch === void 0 ? void 0 : portNumberMatch[0];
        }
        var portLabel = params.row.label;
        if (portNumber && portLabel) {
            return "".concat(portLabel, " : ").concat(portNumber);
        }
        if (!portNumber && portLabel) {
            return portLabel;
        }
        if (!portLabel && portNumber) {
            return portNumber;
        }
        return t('global.ports.n/a');
    }, [t]);
    var renderNodeName = useCallback(function (params) {
        if (params.row.runtime_status.connection_status === 'connected') {
            return permissions.hasNodeUIViewAccess ? (_jsx(OGLink, __assign({ tabIndex: params.tabIndex, variant: "standard", to: "".concat(NODES_ROUTE, "/").concat(params.row.runtime_status.id, "/ports") }, { children: params.row.node_name }))) : (params.row.node_name);
        }
        if (params.row.runtime_status.connection_status === 'disconnected') {
            return (_jsxs(_Fragment, { children: [_jsx(ErrorIcon, { fontSize: "medium", color: "error", sx: { marginRight: 0.5 }, titleAccess: t('components.nodes.state_label.disconnected') }), params.row.node_name] }));
        }
        return (_jsxs(_Fragment, { children: [_jsx(InfoIcon, { fontSize: "medium", sx: { marginRight: 0.5 }, titleAccess: t('components.nodes.state_label.unknown') }), params.row.node_name] }));
    }, [permissions.hasNodeUIViewAccess, t]);
    // Column grid
    var columns = useMemo(function () { return [
        {
            field: 'mode',
            align: 'left',
            headerName: t('pages.ports.table.headers.state'),
            sortable: true,
            minWidth: 90,
            renderCell: function (_a) {
                var port = _a.row;
                return _jsx(PortStateCell, { port: port });
            },
        },
        {
            field: 'label',
            headerName: t('pages.ports.table.headers.label'),
            minWidth: 270,
            sortable: true,
            flex: 1,
            renderCell: renderPortLabel,
        },
        {
            field: 'assignedTags',
            headerName: t('pages.ports.table.headers.resource_tags'),
            minWidth: 300,
            flex: 1,
            sortable: false,
            renderCell: function (_a) {
                var row = _a.row, tabIndex = _a.tabIndex;
                var newTagButton = canEditTags ? (_jsx(NewTagButton, { tabIndex: tabIndex, onClick: function () { return assignTagsToRowClickHandler(row); }, toolTipLabel: t('components.new_tag_button.aria_label') })) : null;
                if (row.assignedTags && row.assignedTags.length > 0) {
                    return (_jsx(ChipPopover, __assign({ tags: row.assignedTags, tabIndex: tabIndex }, { children: newTagButton })));
                }
                return (_jsx(Stack, __assign({ component: "div", tabIndex: tabIndex, "aria-label": t('pages.ports.table.no_tag_message') }, { children: newTagButton })));
            },
        },
        {
            // The sorting for the Node_name column is based on the _id field.
            // Due to the design of the useSortModel, the sorting value is obtained from the field.
            // Therefore, we must use '_id' as the field value here.
            // However, since the node_name cannot be correctly obtained using the field,
            // we need to use renderNodeName to accurately retrieve the node_name.
            field: '_id',
            headerName: t('pages.ports.table.headers.node_name'),
            minWidth: 170,
            flex: 1,
            sortable: true,
            renderCell: renderNodeName,
        },
        {
            field: 'web_terminal',
            headerClassName: 'FocusDisabled',
            maxWidth: 1,
            align: 'center',
            sortable: false,
            renderCell: function (_a) {
                var port = _a.row, tabIndex = _a.tabIndex;
                return (_jsx(WebTerminalCell, { port: port, tabIndex: tabIndex, hasWebTerminalAccess: permissions.hasWebTerminalAccess }));
            },
        },
        {
            field: 'ssh_access',
            headerClassName: 'FocusDisabled',
            maxWidth: 1,
            align: 'center',
            sortable: false,
            renderCell: function (_a) {
                var port = _a.row, tabIndex = _a.tabIndex;
                return _jsx(SshAccessCell, { port: port, tabIndex: tabIndex });
            },
        },
    ]; }, [t, renderPortLabel, renderNodeName, canEditTags, assignTagsToRowClickHandler, permissions.hasWebTerminalAccess]);
    var resourceTagNames = (tagNamesData === null || tagNamesData === void 0 ? void 0 : tagNamesData.resourceTags) || [];
    return (_jsxs(_Fragment, { children: [_jsx(AssignTagModal, { uncheckColumnCheckboxes: uncheckColumnCheckboxes, open: assignTagModalOpen.open, handleClose: assignTagModalOpen.handleClose, selectedRows: modalRowData, resourceTagNames: resourceTagNames }), _jsxs(Box, __assign({ display: "flex", flexWrap: "wrap", marginBottom: 1.5, gap: 1.5 }, { children: [_jsx(PortFilter, { filter: portFilter, setFilter: setPortFilter, permissions: { hasSmartgroupsEditAccess: permissions.hasSmartgroupsEditAccess } }), _jsx(NodeFilter, { filter: nodeFilter, setFilter: setNodeFilter, permissions: { hasSmartgroupsEditAccess: permissions.hasSmartgroupsEditAccess } }), _jsx(PortStatusFilter, { paramFilter: statusPortsParamValue, onChange: setStatusPortsParamValue }), _jsx(Box, { flexGrow: 1 }), _jsx(FreeTextSearch, { onSearch: dispatchTextQuery, useDebounce: false, translations: { tooltip: t('components.nodes.text_search.tooltip') } })] })), _jsx(Box, __assign({ display: "flex", flexWrap: "wrap", marginBottom: 1.5, gap: 1.5 }, { children: _jsx(OGButton, __assign({ disabled: !(rowSelectionModel.length > 0) || !isPrimary || !canEditTags, "aria-label": t('pages.ports.table.button.tag'), variant: "borderless", onClick: assignTagsToSelectedRowsClickHandler, size: "small" }, { children: _jsx(OGTagIcon, { fontSize: "small" }) })) })), _jsx(Box, { children: _jsx(OGTable, { id: "ports-table", "aria-label": t('pages.ports.table.label'), columns: columns, rows: portsRows, autoHeight: true, loading: loading, noRowsMessage: t('pages.ports.table.no_row_message'), pageSizeOptions: pageSizeOptions, pagination: true, paginationMode: "server", paginationModel: paginationModel, onPaginationModelChange: handlePaginationModelChange, rowCount: rowCount, sortingMode: "server", checkboxSelection: permissions.hasTagsEditAccess, onRowSelectionModelChange: setRowSelectionModel, rowSelectionModel: rowSelectionModel, onSortModelChange: handleSortModelChange, columnVisibilityModel: { ssh_access: permissions.hasSerialPortAccess }, initialState: {
                        sorting: {
                            sortModel: defaultSortModel,
                        },
                    } }) })] }));
});
